import { forwardRef } from 'react';
import { Link } from 'react-router';

import { Button, Pill } from '@/components';
import { useAnalytics } from '@/lib/analytics';
import { useIsMobile } from '@/lib/hooks';
import { cn, getSessionLabel } from '@/utils';

import { HOBBY_CLASS_IMAGES } from '../../constants';
import { Schedule } from '../../types';

import {
  getStatusContent,
  getParentConnectStatusContent,
  getIcon,
} from './calendar-item.utils';
import {
  calendarItemVariants,
  calendarPillVariants,
  calendarDateTimeVariants,
  calendarTitleVariants,
} from './calendar-item.variants';
import { ParentConnectAcknowledgement } from './parent-connect-acknowledgement';

const SESSION_LABEL = getSessionLabel();

// Join button is live from 2 min before start until 1 hour after.
const isWithinJoinWindow = (startTime: number): boolean => {
  const now = Date.now() / 1000;
  return now >= startTime - 2 * 60 && now <= startTime + 60 * 60;
};

// Outlined blue pill link used for the Parent Connect row actions
// ("Cancel / Reschedule", "Reschedule").
const ActionLinkPill = ({ to, label }: { to: string; label: string }) => (
  <Link
    to={to}
    className="whitespace-nowrap rounded-full border border-blue-chaos px-3 py-1 md:px-4 md:py-1.5 text-[11px] md:text-[13px] font-semibold text-blue-chaos transition-colors hover:bg-blue-chaos-light-cardbg"
  >
    {label}
  </Link>
);

interface ParentConnectContentProps {
  status?: Schedule.Lesson['status'];
  pcState: Schedule.ParentConnectState;
  pcSessionId?: string;
  startTime: number;
  pcRespondHref?: string;
  pcCancelRescheduleHref?: string;
  pcEditRequestHref?: string;
  pcRescheduleAttemptsLeft?: number;
  isMobile: boolean;
}

// The right-hand content of a Parent Connect / PTM row: the always-shown
// status pill (driven by pcState), optional action pills (active ->
// Cancel/Reschedule; parent-initiated request_sent -> Reschedule), and the
// parent's remaining reschedule budget.
const ParentConnectContent = ({
  status,
  pcState,
  pcSessionId,
  startTime,
  pcRespondHref,
  pcCancelRescheduleHref,
  pcEditRequestHref,
  pcRescheduleAttemptsLeft,
  isMobile,
}: ParentConnectContentProps) => (
  // `relative` so the "attempts left" caption can hang directly below the
  // pill row without adding height to it - that keeps the status / action
  // pills on the same baseline as the date | day | time block. From md up the
  // pills stay on one row (e.g. Acknowledge next to Cancel / Reschedule).
  <div className="relative flex flex-wrap md:flex-nowrap items-center justify-end gap-2 md:gap-3">
    {pcState === 'awaiting_acknowledgement' &&
    pcSessionId &&
    startTime > Date.now() / 1000 ? (
      <ParentConnectAcknowledgement
        key={`${pcSessionId}-${startTime}`}
        sessionId={pcSessionId}
        startTime={startTime}
      />
    ) : (
      <Pill
        variant="solid"
        className={calendarPillVariants({ status, variant: 'parent_connect' })}
        size={isMobile ? 'sm' : 'lg'}
        rounded
      >
        {getParentConnectStatusContent(pcState, pcRespondHref)}
      </Pill>
    )}
    {pcCancelRescheduleHref ? (
      <ActionLinkPill to={pcCancelRescheduleHref} label="Cancel / Reschedule" />
    ) : null}
    {pcEditRequestHref ? (
      <ActionLinkPill to={pcEditRequestHref} label="Reschedule" />
    ) : null}
    {typeof pcRescheduleAttemptsLeft === 'number' ? (
      <span className="absolute right-0 top-full mt-1 whitespace-nowrap text-[9px] md:text-[10px] font-medium text-magical-moonlight-d1">
        Reschedule attempts left: {pcRescheduleAttemptsLeft}
      </span>
    ) : null}
  </div>
);

export interface CalendarItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  date: string;
  day: string;
  time?: string;
  status?: Schedule.Lesson['status'];
  variant?: Schedule.Lesson['variant'];
  start_time?: number;
  joiningLink?: string;
  shouldShowStatus?: boolean;
  pcState?: Schedule.ParentConnectState;
  pcSessionId?: string;
  pcRespondHref?: string;
  pcCancelRescheduleHref?: string;
  pcEditRequestHref?: string;
  pcRescheduleAttemptsLeft?: number;
}

const CalendarItem = forwardRef<HTMLDivElement, CalendarItemProps>(
  (
    {
      className,
      title,
      date,
      day,
      time,
      status,
      variant,
      start_time = 0,
      joiningLink,
      shouldShowStatus,
      pcState,
      pcSessionId,
      pcRespondHref,
      pcCancelRescheduleHref,
      pcEditRequestHref,
      pcRescheduleAttemptsLeft,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const { trackEvent } = useAnalytics();

    const handleJoinClick = () => {
      // Track join button click for hobby/master class
      if (variant === 'hobby_class' || variant === 'master_class') {
        trackEvent('clickedSpecialSessionJoin', 1, {
          type: variant,
          sessionTitle: title,
          sessionDate: date,
          sessionTime: time,
        });
      }

      // Open joining link in new tab
      if (joiningLink) {
        window.open(joiningLink, '_blank');
      }
    };

    const getContent = () => {
      if (variant === 'hobby_class' || variant === 'master_class') {
        return joiningLink && start_time && isWithinJoinWindow(start_time) ? (
          <Button variant="default" onClick={handleJoinClick}>
            Join {SESSION_LABEL.capitalize}
          </Button>
        ) : (
          <div className="w-10" />
        );
      }
      // Parent Connect / PTM: pill is always shown and driven by pcState
      // (independent of the "first upcoming" rule that hides class pills).
      // The row's actions + reschedule budget live in ParentConnectContent.
      if (variant === 'parent_connect' && pcState) {
        return (
          <ParentConnectContent
            status={status}
            pcState={pcState}
            pcSessionId={pcSessionId}
            startTime={start_time}
            pcRespondHref={pcRespondHref}
            pcCancelRescheduleHref={pcCancelRescheduleHref}
            pcEditRequestHref={pcEditRequestHref}
            pcRescheduleAttemptsLeft={pcRescheduleAttemptsLeft}
            isMobile={isMobile}
          />
        );
      }
      if (status && shouldShowStatus) {
        return (
          <Pill
            variant="solid"
            className={calendarPillVariants({ status, variant })}
            size={isMobile ? 'sm' : 'lg'}
            rounded
          >
            {getStatusContent(status)}
          </Pill>
        );
      }
      return <div className="w-10" />;
    };

    return (
      <div
        className={cn(
          calendarItemVariants({ variant, className }),
          'transition-all duration-300',
          status === 'holiday' &&
            'bg-magical-moonlight-s border border-magical-moonlight-l'
        )}
        style={
          variant === 'hobby_class' && HOBBY_CLASS_IMAGES[title]?.colorValue
            ? {
                background: `radial-gradient(circle ${isMobile ? '70px' : '80px'} at ${isMobile ? '4%' : '2%'} ${isMobile ? '25%' : '45%'}, ${HOBBY_CLASS_IMAGES[title].colorValue} 0%, hsl(31.765 15% 22%) 100%)`,
              }
            : undefined
        }
        ref={ref}
        {...props}
      >
        <div className="flex flex-wrap gap-4 items-center justify-between w-full text-xs md:text-base">
          <div className="flex-3 flex gap-6 items-center justify-start">
            {getIcon(variant, status, title, isMobile)}
            <div className={calendarTitleVariants({ variant })}>{title}</div>
          </div>
          <div className="flex-2 flex items-center justify-end">
            {getContent()}
          </div>
          <div className="flex-2 lg:flex-3 flex gap-3 items-center md:flex-initial md:w-auto w-full md:justify-end justify-center min-[400px]:min-w-80">
            <div className={calendarDateTimeVariants({ variant })}>
              <span className="w-1/3 text-center font-semibold leading-6">
                {date}
              </span>
              <span className="text-magical-moonlight-m">|</span>
              <span className="w-1/3 text-center font-semibold leading-6">
                {day}
              </span>
              <span className="text-magical-moonlight-m">|</span>
              <span className="w-1/3 text-center font-semibold leading-6">
                {time ?? '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

CalendarItem.displayName = 'CalendarItem';

export { CalendarItem };
export {
  calendarItemVariants,
  calendarPillVariants,
  calendarDateTimeVariants,
  calendarTitleVariants,
} from './calendar-item.variants';
